import React, { useEffect, useState } from "react";
import { Prohibit, CaretDown, CaretUp, Phone, ArrowSquareOut } from "@phosphor-icons/react";
import { fetchBurnBans } from "../lib/api";

const DANGER_COLORS = {
  Low: "#2A9D8F",
  Moderate: "#4C9AFF",
  High: "#FFB347",
  "Very High": "#F2542D",
  Extreme: "#E63946",
};

function DangerBadge({ level }) {
  if (!level) return null;
  const color = DANGER_COLORS[level] || "#8892A0";
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[12px] font-bold uppercase tracking-wider rounded-sm border"
      style={{ color, borderColor: `${color}55`, background: `${color}15` }}
    >
      {level}
    </span>
  );
}

export default function BurnBanCard({ countySlug, countyName }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);

  useEffect(() => {
    let stop = false;
    setData(null);
    fetchBurnBans(countySlug)
      .then((d) => { if (!stop) setData(d); })
      .catch(() => { if (!stop) setData({ county_ban: null, dnr_areas: [] }); });
    return () => { stop = true; };
  }, [countySlug]);

  if (!data || (!data.county_ban && data.dnr_areas.length === 0)) return null;

  const countyBan = data.county_ban;
  const banActive = countyBan?.status === "BAN";
  const anyDnrBan = data.dnr_areas.some((a) => a.ban_code != null && a.ban_code !== 99);
  const worstDanger = data.dnr_areas[0]?.fire_danger;

  return (
    <section data-testid="burn-ban-card" className="border-b border-tac-border bg-tac-surface/40">
      <button
        type="button"
        data-testid="burn-ban-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <Prohibit
            size={14}
            weight="fill"
            className={banActive || anyDnrBan ? "text-tac-danger" : "text-tac-safe"}
          />
          <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-text">
            Burn Bans · Fire Danger
          </span>
          {banActive && (
            <span
              data-testid="county-ban-pill"
              className="px-1.5 py-0.5 text-[12px] font-bold uppercase tracking-wider rounded-sm bg-tac-danger text-white"
            >
              County Ban
            </span>
          )}
          {!banActive && worstDanger && <DangerBadge level={worstDanger} />}
        </div>
        {open ? (
          <CaretUp size={12} weight="bold" className="text-tac-dim" />
        ) : (
          <CaretDown size={12} weight="bold" className="text-tac-dim" />
        )}
      </button>

      {open && (
        <div className="pb-1">
          {countyBan && (
            <div
              data-testid="county-ban-detail"
              className={`mx-4 mb-2 p-3 rounded-sm border ${
                banActive
                  ? "border-tac-danger/50 bg-tac-danger/10"
                  : "border-tac-border bg-tac-surface/60"
              }`}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  className={`text-[12px] font-bold uppercase tracking-[0.15em] ${
                    banActive ? "text-tac-danger" : "text-tac-warning"
                  }`}
                >
                  {countyBan.county} County-Wide
                </span>
                {countyBan.updated && (
                  <span className="text-[12px] font-mono-tac text-tac-faint">
                    Updated {countyBan.updated}
                  </span>
                )}
              </div>
              <div className="text-[14px] text-tac-text leading-snug">{countyBan.headline}</div>
              <a
                href={countyBan.source_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="county-ban-source-link"
                className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text"
              >
                County page <ArrowSquareOut size={10} weight="bold" />
              </a>
            </div>
          )}

          <ul className="divide-y divide-tac-border/50" data-testid="dnr-ban-list">
            {data.dnr_areas.map((a) => (
              <li key={a.area} data-testid={`burn-ban-area-${a.area}`} className="px-4 py-2">
                <button
                  type="button"
                  onClick={() => setExpandedArea(expandedArea === a.area ? null : a.area)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono-tac text-[14px] text-tac-text truncate group-hover:text-tac-primary transition-colors">
                      {a.area}
                    </span>
                    <DangerBadge level={a.fire_danger} />
                  </div>
                  <div className="text-[13px] font-mono-tac text-tac-dim mt-0.5">
                    {a.ban_code === 99 ? (
                      <span className="text-tac-safe">No burn ban in effect</span>
                    ) : (
                      <span className="text-tac-warning">{a.ban_label}</span>
                    )}
                  </div>
                </button>
                {expandedArea === a.area && (
                  <div className="mt-1.5 text-[13px] text-tac-dim leading-relaxed">
                    {a.notes && <p className="whitespace-pre-line">{a.notes}</p>}
                    {a.region && (
                      <div className="flex items-center gap-1.5 mt-1 text-tac-faint font-mono-tac">
                        <Phone size={10} weight="fill" />
                        DNR {a.region} Region{a.region_phone ? ` · ${a.region_phone}` : ""}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="px-4 py-1.5 text-[11px] font-mono-tac text-tac-faint border-t border-tac-border/40">
        Sources: WA DNR Burn Portal{countySlug === "okanogan" ? " · Okanogan County EM" : ""}
      </div>
    </section>
  );
}
